import React, { useEffect, useRef, lazy, Component } from 'react';
import { MapPinIcon, PhoneIcon, MailIcon, ChevronDownIcon } from 'lucide-react';
export const Contact = () => {
  const contactInfoRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const items = entry.target.querySelectorAll('.contact-item');
          items.forEach((item, index) => {
            setTimeout(() => {
              item.classList.add('animate-feature');
            }, index * 150);
          });
        }
      });
    }, {
      threshold: 0.2
    });
    if (contactInfoRef.current) {
      observer.observe(contactInfoRef.current);
    }
    return () => {
      if (contactInfoRef.current) {
        observer.unobserve(contactInfoRef.current);
      }
    };
  }, []);
  const openWhatsApp = () => {
    const message = encodeURIComponent('Hola, me gustaría obtener más información sobre los paseos en lancha de Perla del Pacífico.');
    window.open(`https://wa.me/593XXXXXXXXX?text=${message}`, '_blank');
  };
  return <section id="contacto" className="section bg-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-64 h-64 bg-turquoise/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-40 left-10 w-96 h-96 bg-navy/10 rounded-full blur-3xl"></div>
      <div className="container relative z-10">
        <div className="text-center mb-16 reveal-up">
          <span className="inline-block text-turquoise font-medium mb-3 transform transition-all duration-700 translate-y-10 opacity-0 animate-title-appear">
            VISÍTANOS
          </span>
          <h2 className="font-playfair text-3xl md:text-5xl font-bold mb-6 transform transition-all duration-700 translate-y-10 opacity-0 animate-title-appear" style={{
          animationDelay: '200ms'
        }}>
            Encuéntranos en el{' '}
            <span className="text-turquoise relative">
              Corazón de Guayaquil
            </span>
          </h2>
          <div className="w-24 h-1 bg-turquoise mx-auto mb-8 transform transition-all duration-700 scale-x-0 animate-line-expand" style={{
          animationDelay: '400ms'
        }}></div>
        </div>
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="reveal-left">
            <div ref={contactInfoRef} className="bg-white p-10 rounded-lg shadow-md relative overflow-hidden border border-navy/10">
              {/* Decorative anchor icon */}
              <div className="absolute -top-10 -right-10 opacity-10">
                <svg width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8V12M12 16V12M12 12H16M12 12H8" stroke="#0D1B2A" strokeWidth="2" strokeLinecap="round" />
                  
                </svg>
              </div>
              <div className="space-y-8 relative z-10">
                <div className="flex items-start contact-item opacity-0">
                  <div className="feature-icon-wrapper mb-4 group">
                    <MapPinIcon className="feature-icon group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="ml-6">
                    <h4 className="font-playfair text-xl font-semibold mb-2">
                      Dirección
                    </h4>
                    <p className="text-navy">
                      Muelle del Yacht Club Naval – Malecón 2000
                    </p>
                    <p className="text-navy/70 text-sm mt-1">
                      Guayaquil, Ecuador
                    </p>
                  </div>
                </div>
                <div className="flex items-start contact-item opacity-0">
                  <div className="feature-icon-wrapper mb-4 group">
                    <PhoneIcon className="feature-icon group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="ml-6">
                    <h4 className="font-playfair text-xl font-semibold mb-2">
                      WhatsApp
                    </h4>
                    <button onClick={openWhatsApp} className="text-navy hover:text-turquoise transition-colors flex items-center">
                      +593 9 XXX XXXX
                      <span className="ml-2 text-xs bg-turquoise/20 text-turquoise py-1 px-2 rounded">
                        Chat ahora
                      </span>
                    </button>
                  </div>
                </div>
                <div className="flex items-start contact-item opacity-0">
                  <div className="feature-icon-wrapper mb-4 group">
                    <MailIcon className="feature-icon group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="ml-6">
                    <h4 className="font-playfair text-xl font-semibold mb-2">
                      Email
                    </h4>
                    <a href="mailto:info@perladelpacifico.ec" className="text-navy hover:text-turquoise transition-colors">
                      info@perladelpacifico.ec
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-12 flex justify-center space-x-4">
                <a href="#" className="social-icon-btn" aria-label="Instagram">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
                <a href="#" className="social-icon-btn" aria-label="Facebook">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385h-3.047v-3.47h3.047v-2.642c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953h-1.514c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385c5.736-.9 10.126-5.864 10.126-11.854z" />
                  </svg>
                </a>
                <a href="#" className="social-icon-btn" aria-label="Twitter">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                </a>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 border-4 border-turquoise rounded-lg z-0 animate-border-pulse"></div>
          </div>
          <div className="relative reveal-right perspective-container">
            <div className="h-96 md:h-[500px] rounded-lg overflow-hidden shadow-lg">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3986.9117465783055!2d-79.88456698524686!3d-2.1972873984058474!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x902d6e649b7a9073%3A0x66c18bd9941f8e4d!2sMalec%C3%B3n%202000!5e0!3m2!1ses!2sec!4v1628189868517!5m2!1ses!2sec" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen loading="lazy" title="Mapa de ubicación" className="w-full h-full"></iframe>
            </div>
            
            
          </div>
        </div>
        <style jsx>{`
          @keyframes titleAppear {
            0% {
              transform: translateY(20px);
              opacity: 0;
            }
            100% {
              transform: translateY(0);
              opacity: 1;
            }
          }
          @keyframes lineExpand {
            0% {
              transform: scaleX(0);
            }
            100% {
              transform: scaleX(1);
            }
          }
          @keyframes textReveal {
            0% {
              transform: translateY(20px);
              opacity: 0;
            }
            100% {
              transform: translateY(0);
              opacity: 1;
            }
          }
          @keyframes featureAppear {
            0% {
              transform: translateY(20px);
              opacity: 0;
            }
            100% {
              transform: translateY(0);
              opacity: 1;
            }
          }
          @keyframes borderPulse {
            0% {
              transform: scale(1);
              opacity: 0.7;
            }
            50% {
              transform: scale(1.05);
              opacity: 1;
            }
            100% {
              transform: scale(1);
              opacity: 0.7;
            }
          }
          .animate-title-appear {
            animation: titleAppear 1s forwards;
          }
          .animate-line-expand {
            animation: lineExpand 1.2s forwards;
          }
          .animate-text-reveal {
            animation: textReveal 1s forwards;
          }
          .animate-feature {
            animation: featureAppear 0.7s forwards;
          }
          .animate-border-pulse {
            animation: borderPulse 4s infinite;
          }
          .animate-border-pulse-delayed {
            animation: borderPulse 4s infinite;
            animation-delay: 2s;
          }
          .perspective-container {
            perspective: 1000px;
          }
        `}</style>
      </div>
    </section>;
};